import axios from "axios";
import axiosRetry from "axios-retry";
import tokenStorage from "../auth/tokenStorage";
import { logout as logoutAuth } from "../redux/authSlice";
import { store } from "../redux/store";
import localStorageAvailable from "../utils/localStorageAvailable";
import logger from "../utils/logger";

const storageAvailable = localStorageAvailable();

const MAX_RETRY_ATTEMPTS = 3;

// Flag to suppress 401 logout during role switch
// When true, 401 errors won't trigger logout (old page requests with stale data are expected to fail)
let isRoleSwitchInProgress = false;
let roleSwitchSafetyTimeout = null;
let isLoggingOut = false;

// Safety net: max time the flag can stay true (prevents indefinite 401 suppression)
const ROLE_SWITCH_MAX_DURATION_MS = 5000;

/**
 * Set the role switch flag
 * @param {boolean} value - true during role switch, false after
 */
export const setRoleSwitchInProgress = (value) => {
  // Clear any existing safety timeout
  if (roleSwitchSafetyTimeout) {
    clearTimeout(roleSwitchSafetyTimeout);
    roleSwitchSafetyTimeout = null;
  }

  isRoleSwitchInProgress = value;
  logger.debug(value ? 'Role switch started' : 'Role switch completed');

  // Safety net: auto-reset flag after max duration to prevent indefinite 401 suppression
  if (value) {
    roleSwitchSafetyTimeout = setTimeout(() => {
      if (isRoleSwitchInProgress) {
        isRoleSwitchInProgress = false;
        logger.warn('Role switch flag auto-reset after timeout (safety net triggered)');
      }
    }, ROLE_SWITCH_MAX_DURATION_MS);
  }
};

export const axiosAuthInstance = axios.create({
  baseURL: import.meta.env.VITE_API_ENDPOINT,
  timeout: 30000,
  withCredentials: tokenStorage.withCredentials(),
  headers: {
    "Content-Type": "application/json",
  },
});

// Safe HTTP methods that can be retried without side effects
const IDEMPOTENT_METHODS = ['get', 'head', 'options'];

// Endpoints allowed to provide explicit role_id (bypassing Redux selectedRole)
// Security: Only these endpoints can override the role_id from Redux state
const EXPLICIT_ROLE_ID_ENDPOINTS = [
  '/app/switch-role',
  '/app/roles',
];

// Helper to safely get retry count from axios-retry internals
// Abstracts internal implementation detail to prevent breakage on library updates
const getRetryCount = (config) => {
  try {
    return config?.['axios-retry']?.retryCount || 0;
  } catch {
    return 0;
  }
};

// Configure axios-retry for automatic retries on 5xx and network errors
axiosRetry(axiosAuthInstance, {
  retries: MAX_RETRY_ATTEMPTS,
  retryDelay: axiosRetry.exponentialDelay, // Exponential backoff with jitter
  shouldResetTimeout: true, // Reset timeout on each retry attempt
  retryCondition: (error) => {
    const method = error.config?.method?.toLowerCase();

    // Only retry idempotent methods to prevent duplicate side effects
    // (e.g., duplicate payments, duplicate record creation)
    if (!IDEMPOTENT_METHODS.includes(method)) {
      return false;
    }

    // Don't retry on 401 (handled separately for auth redirect)
    if (error.response?.status === 401) {
      return false;
    }

    // Retry on network errors or 5xx server errors
    return (
      axiosRetry.isNetworkError(error) ||
      axiosRetry.isRetryableError(error)
    );
  },
  onRetry: (retryCount, error, requestConfig) => {
    logger.warn('Retrying API request', {
      url: requestConfig.url,
      method: requestConfig.method,
      attempt: retryCount,
      maxAttempts: MAX_RETRY_ATTEMPTS,
      reason: !error.response ? 'network_error' : 'server_error',
      status: error.response?.status,
    });
  },
});

axiosAuthInstance.interceptors.request.use(
  (config) => {
    const now = Date.now();

    // Track timing: originalStartTime persists across retries, startTime resets each attempt
    if (!config.metadata?.originalStartTime) {
      config.metadata = { originalStartTime: now, startTime: now };
    } else {
      config.metadata.startTime = now;
    }

    const accessToken = storageAvailable ? tokenStorage.getAccessToken() : "";
    if (!accessToken && tokenStorage.requiresAuthHeader()) {
      if (!isLoggingOut) {
        const isOnLoginPage = window.location.pathname.includes('/auth/login');
        if (!isOnLoginPage) {
          logger.warn('No access token found, redirecting to login');
          window.location.href = "/auth/login-admin";
        }
      }
      return Promise.reject(new Error('No access token - redirecting to login'));
    }
    if (tokenStorage.requiresAuthHeader() && accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    // TODO: Re-enable after backend CORS config updated to allow X-Correlation-ID header
    // Add correlation ID to headers
    // const correlationId = logger.getContext().correlationId;
    // if (correlationId) {
    //   config.headers['X-Correlation-ID'] = correlationId;
    // }

    // include selected role from Redux store
    const { selectedRole } = store.getState().auth;
    if (selectedRole && selectedRole.role_id) {
      const { role_id } = selectedRole;

      // Check if this endpoint is allowed to provide explicit role_id
      // Security: Check pathname ends with whitelisted endpoint (handles API path prefixes)
      const isExplicitRoleAllowed = EXPLICIT_ROLE_ID_ENDPOINTS.some((endpoint) => {
        try {
          const urlPath = new URL(config.url, config.baseURL || 'http://localhost').pathname;
          // Match if path ends with endpoint (e.g., /v1/app/switch-role matches /app/switch-role)
          return urlPath.endsWith(endpoint);
        } catch {
          // Fallback for malformed URLs - default to not allowed
          return false;
        }
      });

      if (config.data && typeof config.data === 'object') {
        const attemptedRoleId = config.data.role_id;

        // Log BEFORE overwriting if non-whitelisted endpoint tried to override role_id
        if (attemptedRoleId && attemptedRoleId !== role_id && !isExplicitRoleAllowed) {
          logger.warn('Blocked explicit role_id on non-whitelisted endpoint', {
            url: config.url,
            attemptedRoleId,
            actualRoleId: role_id,
          });
        }

        // Only allow explicit role_id for whitelisted endpoints (security)
        if (!attemptedRoleId || !isExplicitRoleAllowed) {
          config.data.role_id = role_id;
        }
      } else {
        config.data = { role_id };
      }
    }

    logger.debug('API Request', {
      method: config.method,
      url: config.url,
      hasAuth: !!accessToken,
    });

    return config;
  },
  (error) => {
    logger.error('API Request Error', error);
    return Promise.reject(error);
  }
);

axiosAuthInstance.interceptors.response.use(
  (response) => {
    // Track successful API calls
    const now = Date.now();
    const {metadata} = response.config;
    const attemptDuration = metadata ? now - metadata.startTime : 0;
    const totalDuration = metadata ? now - metadata.originalStartTime : 0;

    logger.metric('api_call', {
      method: response.config.method,
      url: response.config.url,
      status: response.status,
      duration: attemptDuration,
      totalDuration,
      success: true,
    });

    logger.debug('API Response', {
      method: response.config.method,
      url: response.config.url,
      status: response.status,
      duration: attemptDuration,
    });

    return response;
  },
  (error) => {
    const { config, response } = error;
    const now = Date.now();
    const metadata = config?.metadata;
    const attemptDuration = metadata ? now - metadata.startTime : 0;
    const totalDuration = metadata ? now - metadata.originalStartTime : 0;
    const retryCount = getRetryCount(config);

    // Track failed API calls
    logger.metric('api_call', {
      method: config?.method,
      url: config?.url,
      status: response?.status || 'network_error',
      duration: attemptDuration,
      totalDuration,
      success: false,
      retryCount,
    });

    // Handle 401 Unauthorized - clear auth state and redirect to login
    // BUT: During role switch, old page components may make requests with stale data
    // that the new role can't access. Don't logout for these expected failures.
    if (response && response.status === 401) {
      if (isLoggingOut) return Promise.reject(error);

      if (isRoleSwitchInProgress) {
        logger.debug('401 suppressed during role switch (expected for stale requests)', {
          url: config?.url,
        });
        return Promise.reject(error);
      }

      isLoggingOut = true;
      logger.error('Unauthorized request - redirecting to login', error, {
        url: config?.url,
        status: response.status,
      });
      tokenStorage.clearTokens();
      // Dispatch logout to clear Redux auth state (keeps localStorage and Redux in sync)
      store.dispatch(logoutAuth());
      window.location.href = "/auth/login-admin";
      return Promise.reject(error);
    }

    // NOTE: Retry logic is handled by axios-retry (configured above)
    // Manual retry was removed to prevent duplicate retries and inflated metrics

    logger.error('API Request Failed', error, {
      url: config?.url,
      method: config?.method,
      status: response?.status,
      retryCount,
      totalDuration,
    });

    return Promise.reject(error);
  }
);

export default axiosAuthInstance;

export const axiosAuthInstanceForFormData = axios.create({
  baseURL: import.meta.env.VITE_API_ENDPOINT,
  timeout: 30000,
  withCredentials: tokenStorage.withCredentials(),
  headers: {
    "Content-Type": "multipart/form-data",
  },
});

// NOTE: axiosRetry is NOT applied to FormData instance because:
// - File streams are consumed on first read and cannot be re-read
// - FormData cannot be reliably cloned (files become empty)
// - Retrying would send empty file uploads
// Callers should implement their own retry logic with fresh FormData if needed

axiosAuthInstanceForFormData.interceptors.request.use(
  (config) => {
    // Add request start time for metrics
    config.metadata = { startTime: Date.now() };

    const accessToken = storageAvailable ? tokenStorage.getAccessToken() : "";
    if (!accessToken && tokenStorage.requiresAuthHeader()) {
      if (!isLoggingOut) {
        const isOnLoginPage = window.location.pathname.includes('/auth/login');
        if (!isOnLoginPage) {
          logger.warn('No access token found (FormData), redirecting to login');
          window.location.href = "/auth/login-admin";
        }
      }
      return Promise.reject(new Error('No access token - redirecting to login'));
    }
    if (tokenStorage.requiresAuthHeader() && accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    // TODO: Re-enable after backend CORS config updated to allow X-Correlation-ID header
    // Add correlation ID to headers
    // const correlationId = logger.getContext().correlationId;
    // if (correlationId) {
    //   config.headers['X-Correlation-ID'] = correlationId;
    // }

    // include selected role from Redux store
    const { selectedRole: selectedRoleForm } = store.getState().auth;
    if (selectedRoleForm && selectedRoleForm.role_id && config.data && typeof config.data.append === 'function') {
      config.data.append('role_id', selectedRoleForm.role_id);
    }

    logger.debug('API Request (FormData)', {
      method: config.method,
      url: config.url,
      hasAuth: !!accessToken,
    });

    return config;
  },
  (error) => {
    logger.error('API Request Error (FormData)', error);
    return Promise.reject(error);
  }
);

axiosAuthInstanceForFormData.interceptors.response.use(
  (response) => {
    // Track successful API calls
    const duration = response.config.metadata ? Date.now() - response.config.metadata.startTime : 0;
    logger.metric('api_call', {
      method: response.config.method,
      url: response.config.url,
      status: response.status,
      duration,
      success: true,
      contentType: 'multipart/form-data',
    });

    logger.debug('API Response (FormData)', {
      method: response.config.method,
      url: response.config.url,
      status: response.status,
      duration,
    });

    return response;
  },
  (error) => {
    const { config, response } = error;
    const duration = config?.metadata ? Date.now() - config.metadata.startTime : 0;

    // Track failed API calls
    logger.metric('api_call', {
      method: config?.method,
      url: config?.url,
      status: response?.status || 'network_error',
      duration,
      success: false,
      contentType: 'multipart/form-data',
    });

    // Handle 401 Unauthorized - clear auth state and redirect to login
    // BUT: During role switch, suppress logout for expected stale request failures
    if (response && response.status === 401) {
      if (isLoggingOut) return Promise.reject(error);

      if (isRoleSwitchInProgress) {
        logger.debug('401 suppressed during role switch (FormData)', { url: config?.url });
        return Promise.reject(error);
      }

      isLoggingOut = true;
      logger.error('Unauthorized request (FormData) - redirecting to login', error, {
        url: config?.url,
        status: response.status,
      });
      tokenStorage.clearTokens();
      // Dispatch logout to clear Redux auth state (keeps localStorage and Redux in sync)
      store.dispatch(logoutAuth());
      window.location.href = "/auth/login-admin";
      return Promise.reject(error);
    }

    // NOTE: No retry logic for FormData - file streams are consumed on first read
    // and cannot be reliably re-read. Callers should implement their own retry
    // with fresh FormData if needed.

    logger.error('API Request Failed (FormData)', error, {
      url: config?.url,
      method: config?.method,
      status: response?.status,
      reason: !response ? 'network_error' : `http_${response.status}`,
    });

    return Promise.reject(error);
  }
);
